import { takeLatest, put } from 'redux-saga/effects';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import projects from './projects/projects';
import projectImages from './projects/projectImages';
import selectedImages from './selectedImages.js';
import {
  getGalleries,
  getGalleryImages,
  getProject,
  getPublishedProjects,
} from '../../utils/zoomServices';
import imagesToUpload from './imagesToUpload';
import failedImages from './imagesToUpload/failedImages';
import failedImageUploads from './imagesToUpload/failedImageUploads';
import currentlyUploading from './imagesToUpload/currentlyUploading';
import { combineReducers } from '@reduxjs/toolkit';
import galleries from './galleries';
import { renameProject as rp, deleteProject } from '../../utils/apiServices';

const addPhotos = combineReducers({
  projects,
  projectImages,
  selectedImages,
  imagesToUpload,
  failedImages,
  failedImageUploads,
  currentlyUploading,
  galleries,
});

export const LOAD_PROJECTS = 'LOAD_PROJECTS';
export const SET_PROJECTS = 'SET_PROJECTS';
export function loadProjects() {
  return {
    type: LOAD_PROJECTS,
  };
}

export function setProjects(projects) {
  return {
    type: SET_PROJECTS,
    projects,
  };
}

export const LOAD_A_PROJECTS_IMAGES = 'LOAD_A_PROJECTS_IMAGES';
export function loadAProjectsImages(projectID) {
  return {
    type: LOAD_A_PROJECTS_IMAGES,
    projectID,
  };
}

export const SET_A_PROJECTS_IMAGES = 'SET_A_PROJECTS_IMAGES';
export function setProjectsImages(projectID, images) {
  return {
    type: SET_A_PROJECTS_IMAGES,
    projectID,
    images,
  };
}

export const FILES_SELECTED = 'FILES_SELECTED';
export function filesSelected(acceptedCount, rejectedCount) {
  return {
    type: FILES_SELECTED,
    acceptedCount,
    rejectedCount,
  };
}

export const SELECTED_FILES = 'SELECTED_FILES_FOR_UPLOAD';
export function selectedFiles(images, rejected = 0) {
  return {
    type: SELECTED_FILES,
    images,
    rejected,
  };
}

export const DESELECT_FILE = 'DESELECT_FILE';
export function unSelectFile(image) {
  return {
    type: DESELECT_FILE,
    image,
  };
}

export const CLEAR_SELECTED_FILES = 'CLEAR_SELECTED_FILES';
export function clearSelectedFiles() {
  return {
    type: CLEAR_SELECTED_FILES,
  };
}

const LOAD_GALLERIES = 'LOAD_GALLERIES';
export function loadGalleries() {
  return {
    type: LOAD_GALLERIES,
  };
}

const LOAD_GALLERY_IMAGES = 'LOAD_GALLERY_IMAGES';
export function loadGallery(pk) {
  return {
    type: LOAD_GALLERY_IMAGES,
    pk,
  };
}

export const SET_GALLERIES = 'SET_GALLERIES';
export function setGalleries(galleries) {
  return {
    type: SET_GALLERIES,
    galleries,
  };
}

export const SET_GALLERY_IMAGES = 'SET_GALLERY_IMAGES';
function setGalleryImages(images, pk) {
  return {
    type: SET_GALLERY_IMAGES,
    images,
    pk,
  };
}

export const SET_SELECTED_GALLERY = 'SET_SELECTED_GALLERY';
export function setSelectedGallery(name) {
  return {
    type: SET_SELECTED_GALLERY,
    name,
  };
}

export const UPDATE_PROJECT_TITLE = 'UPDATE_PROJECT_TITLE';
export function updateProjectTitle(name, projectPK) {
  return {
    type: UPDATE_PROJECT_TITLE,
    name,
    projectPK,
  };
}

export const DELETE_PROJECT = 'DELETE_PROJECT';
export function deleteProjectAction(projectPK) {
  return {
    type: DELETE_PROJECT,
    projectPK,
  };
}

function* deleteProjectHandler(action) {
  if (!action.projectPK) return;

  yield deleteProject(action.projectPK);
}

function* updateProjectTitleHandler(action) {
  if (!action.projectPK || !action.name || !action.author) return;

  yield rp(action.projectPK, action.name, action.author);
}

function* loadGalleriesHandler() {
  try {
    const galleries = yield getGalleries();
    yield put(setGalleries(galleries));
  } catch (error) {
    console.log(error);
    yield put({ type: 'LOAD_GALLERIES_FAILED', error });
  }
}

function* loadGalleryImagesHandler(action) {
  try {
    const images = yield getGalleryImages(action.pk);
    yield put(setGalleryImages(images, action.pk));
  } catch (error) {
    console.log(error);
    yield put({ type: 'LOAD_GALLERY_IMAGES_FAILED', error });
  }
}

export function* loadProjectsHandler() {
  try {
    const projects = yield getPublishedProjects();
    yield put(setProjects(projects));
  } catch (error) {
    yield put({ type: 'LOAD_PROJECTS_FAILED', error });
  }
}

export function* loadProjectsImagesHandler(action) {
  try {
    const project = yield getProject({ projectPK: action.projectID }, 'images');
    yield put(setProjectsImages(action.projectID, project.images));
  } catch (error) {
    yield put({ type: 'LOAD_USER_PROJECTS_IMAGE_FAILED', error });
  }
}

function* setupSagas() {
  yield takeLatest(LOAD_PROJECTS, loadProjectsHandler);
  yield takeLatest(LOAD_A_PROJECTS_IMAGES, loadProjectsImagesHandler);
  yield takeLatest(LOAD_GALLERIES, loadGalleriesHandler);
  yield takeLatest(LOAD_GALLERY_IMAGES, loadGalleryImagesHandler);
  yield takeLatest(UPDATE_PROJECT_TITLE, updateProjectTitleHandler);
  yield takeLatest(DELETE_PROJECT, deleteProjectHandler);
}

export function useProjects() {
  const dispatch = useDispatch();
  const isInitialLoading = useSelector((state) =>
    state.user.get('isInitialLoading'),
  );
  const isGuest = useSelector((state) => state.user.get('isGuest'));
  const projects = useSelector((state) => state.addPhotos.projects);

  useEffect(() => {
    if (!isInitialLoading && !isGuest && projects === null) {
      dispatch(loadProjects());
    }
  }, [dispatch, isInitialLoading, isGuest, projects]);

  if (isInitialLoading) {
    // isGuest is true by default. Need to wait for it to load
    return null;
  } else if (isGuest) {
    return [];
  } else {
    return projects;
  }
}

export function useCreateProject() {
  const navigate = useNavigate();
  return useCallback(() => navigate('/create'), [navigate]);
}

export function useProjectsLoading() {
  const dispatch = useDispatch();
  const projects = useSelector((state) => state.addPhotos.projects);

  const refreshProjects = useCallback(() => {
    dispatch(loadProjects());
  }, [dispatch]);

  const isLoading = projects === null;
  const isEmpty = Array.isArray(projects) && projects.length === 0;

  return {
    isLoading,
    isEmpty,
    refreshProjects,
  };
}

/** ***********************************************************************
 *  Main Export
 *  *********************************************************************** */
export default {
  reducer: addPhotos,
  saga: setupSagas,
};
